import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { scriptService, Script } from '../services/scriptService';
import { authService } from '../services/auth';
import { useUser } from '../contexts/UserContext';
import MyVoiceTab from '../components/voice/MyVoiceTab';
import { Icon } from '../components/common/Icon';
import { FaEdit, FaTrash, FaCoins } from 'react-icons/fa';
import { config } from '../config';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #cccccc;
  margin: 0;
`;

const CreateButton = styled(Link)`
  display: inline-block;
  background-color: #4CAF50;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s;
  margin-top: 20px;

  &:hover {
    background-color: #45a049;
    transform: translateY(-1px);
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
`;

const Tab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: 10px 20px;
  font-size: 1rem;
  color: ${props => props.$active ? '#4CAF50' : '#666'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? '#4CAF50' : 'transparent'};
  transition: all 0.2s;

  &:hover {
    color: #4CAF50;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
`;

const CardContent = styled.div`
  padding: 20px;
`;

const CardTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  color: #ffffff;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const CardDescription = styled.p`
  margin: 0 0 10px 0;
  color: #cccccc;
  font-size: 0.9rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #333;
  font-size: 0.9rem;
  color: #cccccc;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const StatusBadge = styled.span<{ isDraft?: boolean }>`
  background-color: ${props => props.isDraft ? '#ffa500' : '#4CAF50'};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #d32f2f;
  font-size: 1.1rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
  font-size: 1.1rem;
`;

// Modal components
const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 12px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: #ffffff;
  margin: 0 0 15px 0;
  font-size: 1.3rem;
  font-weight: 600;
`;

const ModalText = styled.p`
  color: #cccccc;
  margin: 0 0 25px 0;
  line-height: 1.5;
  font-size: 1rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  min-width: 80px;
  
  ${props => props.$variant === 'danger' ? `
    background-color: #dc3545;
    color: white;
    &:hover {
      background-color: #c82333;
      transform: translateY(-1px);
    }
  ` : `
    background-color: #666;
    color: white;
    &:hover {
      background-color: #777;
      transform: translateY(-1px);
    }
  `}
`;

const ScriptName = styled.span`
  font-weight: 600;
  color: #ffffff;
`;

const CardActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background-color: #4CAF50;
          color: white;
          &:hover { 
            background-color: #45a049; 
            transform: translateY(-1px);
          }
        `;
      case 'danger':
        return `
          background-color: #f44336;
          color: white;
          &:hover { 
            background-color: #d32f2f;
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background-color: #e0e0e0;
          color: #333;
          &:hover { 
            background-color: #d0d0d0;
            transform: translateY(-1px);
          }
        `;
    }
  }}
`;

const ActionLink = styled(Link)<{ $variant?: 'primary' | 'danger' }>`
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background-color: #4CAF50;
          color: white;
          &:hover { 
            background-color: #45a049;
            transform: translateY(-1px);
          }
        `;
      case 'danger':
        return `
          background-color: #f44336;
          color: white;
          &:hover { 
            background-color: #d32f2f;
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background-color: #e0e0e0;
          color: #333;
          &:hover { 
            background-color: #d0d0d0;
            transform: translateY(-1px);
          }
        `;
    }
  }}
`;

const ProfilePicture = styled.div<{ $imageUrl: string }>`
  width: 128px;
  height: 128px;
  border-radius: 50%;
  background-image: url(${props => props.$imageUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 0px ;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;


`;

// Settings UI Components
const SettingsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SettingsSection = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 30px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #ffffff;
  margin: 0 0 20px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 8px;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
  background-color: #1a1a1a;
  color: #ffffff;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &:disabled {
    background-color: #333;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #888;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s;
  background-color: #1a1a1a;
  color: #ffffff;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #888;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  color: #cccccc;
  font-size: 0.95rem;
`;

// Search and Sort UI Components
const SearchSortContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 10px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 1rem;
  background-color: #1a1a1a;
  color: #ffffff;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #888;
  }
`;

const SortBubbles = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SortBubble = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: 1px solid #666;
  border-radius: 20px;
  background-color: ${props => props.$active ? '#4CAF50' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#cccccc'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: #4CAF50;
    color: white;
  }
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1rem;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SaveButton = styled.button`
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #45a049;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const CheckButton = styled.button`
  background-color: #2196F3;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #1976D2;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const RegenerateButton = styled.button`
  background-color: #9C27B0;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #7B1FA2;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 0.9rem;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background-color: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #a5d6a7;
        `;
      case 'error':
        return `
          background-color: #ffebee;
          color: #c62828;
          border: 1px solid #ef9a9a;
        `;
      case 'info':
        return `
          background-color: #e3f2fd;
          color: #1565c0;
          border: 1px solid #90caf9;
        `;
    }
  }}
`;

const AvailabilityMessage = styled.span<{ available?: boolean }>`
  margin-left: 10px;
  font-size: 0.9rem;
  color: ${props => props.available ? '#4CAF50' : '#f44336'};
`;

const You: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoggedIn, user, refreshUser } = useUser();
  const activeTab = searchParams.get('tab') || 'scripts';
  
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New meditation results state for My Meditations and Favorites
  const [myMeditations, setMyMeditations] = useState<MeditationResultItem[]>([]);
  const [favorites, setFavorites] = useState<MeditationResultItem[]>([]);
  const [myMeditationsLoading, setMyMeditationsLoading] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  
  // Search and sort state
  const [myMeditationsSearch, setMyMeditationsSearch] = useState('');
  const [favoritesSearch, setFavoritesSearch] = useState('');
  const [myMeditationsSort, setMyMeditationsSort] = useState('published_at_desc'); // default newest first
  const [favoritesSort, setFavoritesSort] = useState('created_at_desc'); // default most recent first
  
  // Debounce refs for search
  const myMeditationsSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const favoritesSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Refs to get current search values in timeouts
  const currentMyMeditationsSearch = useRef(myMeditationsSearch);
  const currentFavoritesSearch = useRef(favoritesSearch);
  
  // Update refs when state changes
  useEffect(() => {
    currentMyMeditationsSearch.current = myMeditationsSearch;
  }, [myMeditationsSearch]);
  
  useEffect(() => {
    currentFavoritesSearch.current = favoritesSearch;
  }, [favoritesSearch]);
  
  // Infinite scroll state for My Meditations
  const [myMeditationsHasMore, setMyMeditationsHasMore] = useState(true);
  const [myMeditationsOffset, setMyMeditationsOffset] = useState(0);
  const [myMeditationsTotalLoaded, setMyMeditationsTotalLoaded] = useState(0);
  
  // Infinite scroll state for Favorites
  const [favoritesHasMore, setFavoritesHasMore] = useState(true);
  const [favoritesOffset, setFavoritesOffset] = useState(0);
  const [favoritesTotalLoaded, setFavoritesTotalLoaded] = useState(0);
  
  const maxResults = 100;
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState<Script | null>(null);

  // Settings state
  const [settingsForm, setSettingsForm] = useState({
    username: user?.username || '',
    about_me: user?.profile_settings?.about_me || '',
    dont_show_scripts: user?.profile_settings?.dont_show_scripts || false,
    dont_show_favorites: user?.profile_settings?.dont_show_favorites || false,
    dont_show_comments: user?.profile_settings?.dont_show_comments || false,
  });
  const [profileImagePrompt, setProfileImagePrompt] = useState(user?.profile_image_prompt || '');
  const [usernameAvailability, setUsernameAvailability] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Get profile image URL with fallback to default
  let profileImageUrl =  '/images/profile/default256.png';
  let profileImage = user?.profile_image;
  if (profileImage){
    
    profileImageUrl = `https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/512/${profileImage}` 
  }

  

  const userName = user?.username;

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  const fetchScripts = useCallback(async () => {
    if (!isLoggedIn) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const userScripts = await scriptService.getUserScripts();
      setScripts(userScripts);
    } catch (err) {
      console.error('Error fetching scripts:', err);
      setError('Failed to load scripts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // This useEffect will be replaced by individual ones after the fetch functions are defined

  // Update settings form when user data changes
  useEffect(() => {
    if (user) {
      setSettingsForm({
        username: user.username || '',
        about_me: user.profile_settings?.about_me || '',
        dont_show_scripts: user.profile_settings?.dont_show_scripts || false,
        dont_show_favorites: user.profile_settings?.dont_show_favorites || false,
        dont_show_comments: user.profile_settings?.dont_show_comments || false,
      });
      setProfileImagePrompt(user.profile_image_prompt || '');
    }
  }, [user]);

  const handleDeleteScript = (script: Script) => {
    setScriptToDelete(script);
    setShowDeleteModal(true);
  };

  const confirmDeleteScript = async () => {
    if (!scriptToDelete) return;
    
    try {
      await scriptService.deleteScript(scriptToDelete.id);
      await fetchScripts(); // Refresh the list
      setShowDeleteModal(false);
      setScriptToDelete(null);
    } catch (err) {
      console.error('Error deleting script:', err);
      alert('Failed to delete script. Please try again.');
    }
  };

  const cancelDeleteScript = () => {
    setShowDeleteModal(false);
    setScriptToDelete(null);
  };

  // Settings handlers
  const handleCheckUsernameAvailability = async () => {
    if (!settingsForm.username || settingsForm.username === user?.username) {
      return;
    }

    setCheckingUsername(true);
    setUsernameAvailability(null);
    
    try {
      const available = await authService.checkUsernameAvailability(settingsForm.username);
      setUsernameAvailability(available);
    } catch (err) {
      console.error('Error checking username:', err);
      setSettingsMessage({ type: 'error', text: 'Failed to check username availability' });
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setSettingsMessage(null);

    try {
      await authService.updateProfileSettings({
        username: settingsForm.username,
        about_me: settingsForm.about_me,
        dont_show_scripts: settingsForm.dont_show_scripts,
        dont_show_favorites: settingsForm.dont_show_favorites,
        dont_show_comments: settingsForm.dont_show_comments,
      });

      await refreshUser();
      setSettingsMessage({ type: 'success', text: 'Settings saved successfully!' });
      setUsernameAvailability(null);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setSettingsMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Failed to save settings' 
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRegenerateImage = async () => {
    if (!profileImagePrompt.trim()) {
      setSettingsMessage({ type: 'error', text: 'Please enter a prompt for your profile image' });
      return;
    }

    setRegeneratingImage(true);
    setSettingsMessage(null);

    try {
      const result = await authService.regenerateProfileImage(profileImagePrompt);
      
      // Wait 2 seconds for Lambda resize functions to process the new image
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await refreshUser();
      setSettingsMessage({ 
        type: 'success', 
        text: `Profile image regenerated! ${result.coins_spent} coins spent. Remaining: ${result.remaining_coins}` 
      });
    } catch (err: any) {
      console.error('Error regenerating image:', err);
      setSettingsMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Failed to regenerate profile image' 
      });
    } finally {
      setRegeneratingImage(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // My Meditations API functions
  const buildMyMeditationsParams = (currentOffset: number, search?: string, sort?: string) => {
    const apiParams = new URLSearchParams();
    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');
    if (search && search.trim()) {
      apiParams.append('keyword', search.trim());
    }
    if (sort) {
      apiParams.append('sort', sort);
    }
    return apiParams;
  };

  const fetchMyMeditations = useCallback(async (reset = true) => {
    if (!isLoggedIn || !user?.id) return;
    
    setMyMeditationsLoading(true);
    
    try {
      const currentOffset = reset ? 0 : myMeditationsOffset;
      const apiParams = buildMyMeditationsParams(currentOffset, myMeditationsSearch, myMeditationsSort);
      
      const response = await fetch(`${config.apiUrl}/scripts/user/${user.id}?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch meditations');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      if (reset) {
        setMyMeditations(meditations);
        setMyMeditationsOffset(meditations.length);
        setMyMeditationsTotalLoaded(meditations.length);
      } else {
        setMyMeditations(prev => [...prev, ...meditations]);
        setMyMeditationsOffset(prev => prev + meditations.length);
        setMyMeditationsTotalLoaded(prev => prev + meditations.length);
      }
      
      setMyMeditationsHasMore(meditations.length === 20 && (reset ? meditations.length : myMeditationsTotalLoaded + meditations.length) < maxResults);
    } catch (error) {
      console.error('Error fetching my meditations:', error);
      if (reset) {
        setMyMeditations([]);
      }
      setMyMeditationsHasMore(false);
    } finally {
      setMyMeditationsLoading(false);
    }
  }, [isLoggedIn, user?.id, myMeditationsSearch, myMeditationsSort, myMeditationsOffset, myMeditationsTotalLoaded, maxResults]);

  const loadMoreMyMeditations = useCallback(async () => {
    if (myMeditationsLoading || !myMeditationsHasMore || myMeditationsTotalLoaded >= maxResults) return;
    fetchMyMeditations(false);
  }, [myMeditationsLoading, myMeditationsHasMore, myMeditationsTotalLoaded, maxResults, fetchMyMeditations]);

  // Favorites API functions
  const buildFavoritesParams = (currentOffset: number, search?: string, sort?: string) => {
    const apiParams = new URLSearchParams();
    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');
    if (search && search.trim()) {
      apiParams.append('keyword', search.trim());
    }
    if (sort) {
      apiParams.append('sort', sort);
    }
    return apiParams;
  };

  const fetchFavorites = useCallback(async (reset = true) => {
    if (!isLoggedIn || !user?.id) return;
    
    setFavoritesLoading(true);
    
    try {
      const currentOffset = reset ? 0 : favoritesOffset;
      const apiParams = buildFavoritesParams(currentOffset, favoritesSearch, favoritesSort);
      
      const response = await fetch(`${config.apiUrl}/scripts/favorites?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      if (reset) {
        setFavorites(meditations);
        setFavoritesOffset(meditations.length);
        setFavoritesTotalLoaded(meditations.length);
      } else {
        setFavorites(prev => [...prev, ...meditations]);
        setFavoritesOffset(prev => prev + meditations.length);
        setFavoritesTotalLoaded(prev => prev + meditations.length);
      }
      
      setFavoritesHasMore(meditations.length === 20 && (reset ? meditations.length : favoritesTotalLoaded + meditations.length) < maxResults);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      if (reset) {
        setFavorites([]);
      }
      setFavoritesHasMore(false);
    } finally {
      setFavoritesLoading(false);
    }
  }, [isLoggedIn, user?.id, favoritesSearch, favoritesSort, favoritesOffset, favoritesTotalLoaded, maxResults]);

  const loadMoreFavorites = useCallback(async () => {
    if (favoritesLoading || !favoritesHasMore || favoritesTotalLoaded >= maxResults) return;
    fetchFavorites(false);
  }, [favoritesLoading, favoritesHasMore, favoritesTotalLoaded, maxResults, fetchFavorites]);

  // Dedicated search function that uses current values
  const performMyMeditationsSearch = useCallback(() => {
    if (!isLoggedIn || !user?.id) return;
    
    setMyMeditationsLoading(true);
    setMyMeditations([]);
    setMyMeditationsOffset(0);
    setMyMeditationsTotalLoaded(0);
    setMyMeditationsHasMore(true);
    
    const apiParams = buildMyMeditationsParams(0, currentMyMeditationsSearch.current, myMeditationsSort);
    
    fetch(`${config.apiUrl}/scripts/user/${user.id}?${apiParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${authService.getToken() || ''}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch meditations');
      }
      return response.json();
    })
    .then(data => {
      const meditations = data.meditations || [];
      setMyMeditations(meditations);
      setMyMeditationsOffset(meditations.length);
      setMyMeditationsTotalLoaded(meditations.length);
      setMyMeditationsHasMore(meditations.length === 20 && meditations.length < maxResults);
    })
    .catch(error => {
      console.error('Error fetching my meditations:', error);
      setMyMeditations([]);
      setMyMeditationsHasMore(false);
    })
    .finally(() => {
      setMyMeditationsLoading(false);
    });
  }, [isLoggedIn, user?.id, myMeditationsSort, maxResults]);

  // Dedicated search function for favorites
  const performFavoritesSearch = useCallback(() => {
    if (!isLoggedIn || !user?.id) return;
    
    setFavoritesLoading(true);
    setFavorites([]);
    setFavoritesOffset(0);
    setFavoritesTotalLoaded(0);
    setFavoritesHasMore(true);
    
    const apiParams = buildFavoritesParams(0, currentFavoritesSearch.current, favoritesSort);
    
    fetch(`${config.apiUrl}/scripts/favorites?${apiParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${authService.getToken() || ''}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }
      return response.json();
    })
    .then(data => {
      const meditations = data.meditations || [];
      setFavorites(meditations);
      setFavoritesOffset(meditations.length);
      setFavoritesTotalLoaded(meditations.length);
      setFavoritesHasMore(meditations.length === 20 && meditations.length < maxResults);
    })
    .catch(error => {
      console.error('Error fetching favorites:', error);
      setFavorites([]);
      setFavoritesHasMore(false);
    })
    .finally(() => {
      setFavoritesLoading(false);
    });
  }, [isLoggedIn, user?.id, favoritesSort, maxResults]);

  // Search handlers
  const handleMyMeditationsSearch = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Clear timeout and search immediately on Enter
      if (myMeditationsSearchTimeout.current) {
        clearTimeout(myMeditationsSearchTimeout.current);
      }
      performMyMeditationsSearch();
    }
  }, [performMyMeditationsSearch]);

  const handleMyMeditationsSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setMyMeditationsSearch(newValue);
    
    // Clear existing timeout
    if (myMeditationsSearchTimeout.current) {
      clearTimeout(myMeditationsSearchTimeout.current);
    }
    
    // Set new timeout for debounced search
    myMeditationsSearchTimeout.current = setTimeout(() => {
      performMyMeditationsSearch();
    }, 300);
  }, [performMyMeditationsSearch]);

  const handleFavoritesSearch = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Clear timeout and search immediately on Enter
      if (favoritesSearchTimeout.current) {
        clearTimeout(favoritesSearchTimeout.current);
      }
      performFavoritesSearch();
    }
  }, [performFavoritesSearch]);

  const handleFavoritesSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFavoritesSearch(newValue);
    
    // Clear existing timeout
    if (favoritesSearchTimeout.current) {
      clearTimeout(favoritesSearchTimeout.current);
    }
    
    // Set new timeout for debounced search
    favoritesSearchTimeout.current = setTimeout(() => {
      performFavoritesSearch();
    }, 300);
  }, [performFavoritesSearch]);

  // Sort handlers
  const handleMyMeditationsSort = useCallback((sort: string) => {
    setMyMeditationsSort(sort);
  }, []);

  const handleFavoritesSort = useCallback((sort: string) => {
    setFavoritesSort(sort);
  }, []);

  // Initial tab loading
  useEffect(() => {
    if (activeTab === 'scripts') {
      fetchMyMeditations(true);
    } else if (activeTab === 'drafts') {
      fetchScripts();
    } else if (activeTab === 'favorites') {
      fetchFavorites(true);
    }
  }, [activeTab]);

  // Effect to trigger refetch when sort changes (separate from search changes)
  useEffect(() => {
    if (activeTab === 'scripts' && myMeditationsSort) {
      fetchMyMeditations(true);
    }
  }, [myMeditationsSort]);

  useEffect(() => {
    if (activeTab === 'favorites' && favoritesSort) {
      fetchFavorites(true);
    }
  }, [favoritesSort]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (myMeditationsSearchTimeout.current) {
        clearTimeout(myMeditationsSearchTimeout.current);
      }
      if (favoritesSearchTimeout.current) {
        clearTimeout(favoritesSearchTimeout.current);
      }
    };
  }, []);

  // Set up infinite scroll for My Meditations
  const { sentinelRef: myMeditationsSentinelRef, loadingRef: myMeditationsLoadingRef } = useInfiniteScroll({
    loadMore: loadMoreMyMeditations,
    hasMore: myMeditationsHasMore && myMeditationsTotalLoaded < maxResults,
    loading: myMeditationsLoading
  });

  // Set up infinite scroll for Favorites
  const { sentinelRef: favoritesSentinelRef, loadingRef: favoritesLoadingRef } = useInfiniteScroll({
    loadMore: loadMoreFavorites,
    hasMore: favoritesHasMore && favoritesTotalLoaded < maxResults,
    loading: favoritesLoading
  });

  const getFilteredScripts = () => {
    switch (activeTab) {
      case 'drafts':
        return scripts.filter(script => script.is_draft);
      default:
        return [];
    }
  };

  const renderScriptCard = (script: Script) => (
    <Card key={script.id}>
      <CardContent>
        <CardTitle>{script.script_name}</CardTitle>
        <CardDescription>
          {script.script_description || 'No description provided'}
        </CardDescription>
        <CardMeta>
          <span>Created {formatDate(script.created_at)}</span>
          <StatusBadge isDraft={script.is_draft}>
            {script.is_draft ? 'Draft' : 'Published'}
          </StatusBadge>
        </CardMeta>
        <CardActions>
          <ActionLink $variant="primary" to={`/edit/${script.id}`} title="Edit Script">
            <Icon icon={FaEdit} />
          </ActionLink>
          
          <ActionButton 
            $variant="danger"
            onClick={() => handleDeleteScript(script)}
            title="Delete Script"
          >
            <Icon icon={FaTrash} />
          </ActionButton>
        </CardActions>
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    if (loading) {
      return <LoadingMessage>Loading scripts...</LoadingMessage>;
    }

    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>;
    }

    const filteredScripts = getFilteredScripts();

    if (filteredScripts.length === 0) {
      const emptyMessage = "You don't have any draft scripts yet. Create a new script to get started!";
      
      return (
        <div>
          <EmptyMessage>{emptyMessage}</EmptyMessage>
          <div style={{ textAlign: 'center' }}>
            <CreateButton to="/create">Create New Script</CreateButton>
          </div>
        </div>
      );
    }

    return (
      <div>
        <Grid>
          {filteredScripts.map(renderScriptCard)}
        </Grid>
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <CreateButton to="/create">Create New Script</CreateButton>
        </div>
      </div>
    );
  };

  return (
    <Container>
      <Header>
        <Title>Your Profile ({userName})</Title>
        <ProfilePicture $imageUrl={profileImageUrl} />
      </Header>

      <Tabs>
        <Tab 
          $active={activeTab === 'scripts'} 
          onClick={() => handleTabChange('scripts')}
        >
          My Meditations
        </Tab>
        <Tab 
          $active={activeTab === 'drafts'} 
          onClick={() => handleTabChange('drafts')}
        >
          My Drafts
        </Tab>
        <Tab 
          $active={activeTab === 'favorites'} 
          onClick={() => handleTabChange('favorites')}
        >
          Favorites
        </Tab>
        <Tab
          $active={activeTab === 'my-voice'}
          onClick={() => handleTabChange('my-voice')}
        >
          Voices
        </Tab>
        <Tab
          $active={activeTab === 'settings'}
          onClick={() => handleTabChange('settings')}
        >
          Settings
        </Tab>
      </Tabs>

      {activeTab === 'scripts' && (
        <div>
          <SearchSortContainer>
            <SearchInput
              type="text"
              placeholder="Search my meditations..."
              value={myMeditationsSearch}
              onChange={handleMyMeditationsSearchChange}
              onKeyDown={handleMyMeditationsSearch}
            />
            <SortBubbles>
              <SortBubble 
                $active={myMeditationsSort === 'published_at_desc'} 
                onClick={() => handleMyMeditationsSort('published_at_desc')}
              >
                New
              </SortBubble>
              <SortBubble 
                $active={myMeditationsSort === 'published_at_asc'} 
                onClick={() => handleMyMeditationsSort('published_at_asc')}
              >
                Old
              </SortBubble>
              <SortBubble 
                $active={myMeditationsSort === 'rating_desc'} 
                onClick={() => handleMyMeditationsSort('rating_desc')}
              >
                Popular
              </SortBubble>
            </SortBubbles>
          </SearchSortContainer>

          <MeditationResults 
            meditations={myMeditations} 
            loading={false}
          />

          {/* Infinite scroll loading indicator */}
          {myMeditationsLoading && myMeditations.length > 0 && (
            <LoadingIndicator ref={myMeditationsLoadingRef}>
              Loading more meditations...
            </LoadingIndicator>
          )}

          {/* Infinite scroll sentinel */}
          {myMeditationsHasMore && myMeditationsTotalLoaded < maxResults && !myMeditationsLoading && (
            <InfiniteScrollSentinel ref={myMeditationsSentinelRef} />
          )}

          {/* Show message when no results */}
          {!myMeditationsLoading && myMeditations.length === 0 && (
            <NoResultsMessage>
              {myMeditationsSearch.trim() ? 'No meditations found matching your search.' : 'You haven\'t created any published meditations yet.'}
            </NoResultsMessage>
          )}
        </div>
      )}

      {activeTab === 'drafts' && renderContent()}

      {activeTab === 'favorites' && (
        <div>
          <SearchSortContainer>
            <SearchInput
              type="text"
              placeholder="Search favorites..."
              value={favoritesSearch}
              onChange={handleFavoritesSearchChange}
              onKeyDown={handleFavoritesSearch}
            />
            <SortBubbles>
              <SortBubble 
                $active={favoritesSort === 'created_at_desc'} 
                onClick={() => handleFavoritesSort('created_at_desc')}
              >
                Recent
              </SortBubble>
              <SortBubble 
                $active={favoritesSort === 'created_at_asc'} 
                onClick={() => handleFavoritesSort('created_at_asc')}
              >
                Old
              </SortBubble>
              <SortBubble 
                $active={favoritesSort === 'listen_count_desc'} 
                onClick={() => handleFavoritesSort('listen_count_desc')}
              >
                Most Listens
              </SortBubble>
              <SortBubble 
                $active={favoritesSort === 'listen_count_asc'} 
                onClick={() => handleFavoritesSort('listen_count_asc')}
              >
                Least Listens
              </SortBubble>
            </SortBubbles>
          </SearchSortContainer>

          <MeditationResults 
            meditations={favorites} 
            loading={false}
          />

          {/* Infinite scroll loading indicator */}
          {favoritesLoading && favorites.length > 0 && (
            <LoadingIndicator ref={favoritesLoadingRef}>
              Loading more favorites...
            </LoadingIndicator>
          )}

          {/* Infinite scroll sentinel */}
          {favoritesHasMore && favoritesTotalLoaded < maxResults && !favoritesLoading && (
            <InfiniteScrollSentinel ref={favoritesSentinelRef} />
          )}

          {/* Show message when no results */}
          {!favoritesLoading && favorites.length === 0 && (
            <NoResultsMessage>
              {favoritesSearch.trim() ? 'No favorites found matching your search.' : 'You haven\'t favorited any meditations yet.'}
            </NoResultsMessage>
          )}
        </div>
      )}

      {activeTab === 'my-voice' && (
        <MyVoiceTab />
      )}

      {activeTab === 'settings' && (
        <SettingsContainer>
          {settingsMessage && (
            <Message type={settingsMessage.type}>
              {settingsMessage.text}
            </Message>
          )}

          <SettingsSection>
            <SectionTitle>Profile Information</SectionTitle>
            
            <FormGroup>
              <Label htmlFor="username">Username</Label>
              <ButtonGroup>
                <Input
                  id="username"
                  type="text"
                  value={settingsForm.username}
                  onChange={(e) => {
                    setSettingsForm({ ...settingsForm, username: e.target.value });
                    setUsernameAvailability(null);
                  }}
                  placeholder="Enter your username"
                />
                <CheckButton 
                  onClick={handleCheckUsernameAvailability}
                  disabled={checkingUsername || !settingsForm.username || settingsForm.username === user?.username}
                >
                  {checkingUsername ? 'Checking...' : 'Check availability'}
                </CheckButton>
                {usernameAvailability !== null && (
                  <AvailabilityMessage available={usernameAvailability}>
                    {usernameAvailability ? '✓ Available' : '✗ Already taken'}
                  </AvailabilityMessage>
                )}
              </ButtonGroup>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="aboutMe">About Me</Label>
              <TextArea
                id="aboutMe"
                value={settingsForm.about_me}
                onChange={(e) => setSettingsForm({ ...settingsForm, about_me: e.target.value })}
                placeholder="Tell us about yourself..."
                rows={4}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="profilePrompt">Profile Image Prompt</Label>
              <TextArea
                id="profilePrompt"
                value={profileImagePrompt}
                onChange={(e) => setProfileImagePrompt(e.target.value)}
                placeholder="Describe your ideal profile image..."
                rows={3}
              />
              <ButtonGroup style={{ marginTop: '10px' }}>
                <RegenerateButton 
                  onClick={handleRegenerateImage}
                  disabled={regeneratingImage || !profileImagePrompt.trim()}
                >
                  <Icon icon={FaCoins} />
                  {regeneratingImage ? 'Generating...' : (<>Re-generate Image 150 <img src="/images/YMcoin.png" alt="coin" style={{width: '16px', height: '16px', marginLeft: '2px', verticalAlign: 'middle'}} /></>)}
                </RegenerateButton>
              </ButtonGroup>
            </FormGroup>
          </SettingsSection>

          <SettingsSection>
            <SectionTitle>Privacy Settings</SectionTitle>
            
            <CheckboxGroup>
              <Checkbox
                id="dontShowScripts"
                type="checkbox"
                checked={settingsForm.dont_show_scripts}
                onChange={(e) => setSettingsForm({ ...settingsForm, dont_show_scripts: e.target.checked })}
              />
              <CheckboxLabel htmlFor="dontShowScripts">
                Don't list my meditations on my public profile
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <Checkbox
                id="dontShowFavorites"
                type="checkbox"
                checked={settingsForm.dont_show_favorites}
                onChange={(e) => setSettingsForm({ ...settingsForm, dont_show_favorites: e.target.checked })}
              />
              <CheckboxLabel htmlFor="dontShowFavorites">
                Don't list my favorites on my public profile
              </CheckboxLabel>
            </CheckboxGroup>

            <CheckboxGroup>
              <Checkbox
                id="dontShowComments"
                type="checkbox"
                checked={settingsForm.dont_show_comments}
                onChange={(e) => setSettingsForm({ ...settingsForm, dont_show_comments: e.target.checked })}
              />
              <CheckboxLabel htmlFor="dontShowComments">
                Don't allow comments on my public profile
              </CheckboxLabel>
            </CheckboxGroup>
          </SettingsSection>

          <ButtonGroup style={{ marginTop: '20px', justifyContent: 'center' }}>
            <SaveButton 
              onClick={handleSaveSettings}
              disabled={savingSettings || (settingsForm.username !== user?.username && usernameAvailability === false)}
            >
              {savingSettings ? 'Saving...' : 'Save All Settings'}
            </SaveButton>
          </ButtonGroup>
        </SettingsContainer>
      )}

      {/* Delete Confirmation Modal */}
      <Modal $isOpen={showDeleteModal}>
        <ModalContent>
          <ModalTitle>Delete Script</ModalTitle>
          <ModalText>
            Are you sure you want to delete <ScriptName>"{scriptToDelete?.script_name}"</ScriptName>? 
            This action cannot be undone.
          </ModalText>
          <ModalButtons>
            <ModalButton onClick={cancelDeleteScript}>
              Cancel
            </ModalButton>
            <ModalButton $variant="danger" onClick={confirmDeleteScript}>
              Delete
            </ModalButton>
          </ModalButtons>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default You; 